import { FC, ReactNode, useEffect, useMemo } from 'react';
import { ConnectionProvider, WalletProvider, useWallet } from '@solana/wallet-adapter-react';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { SolflareWalletAdapter } from '@solana/wallet-adapter-wallets';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";
import '@solana/wallet-adapter-react-ui/styles.css';

interface Props {
  children: ReactNode;
}

const WalletAuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { publicKey } = useWallet();
  const { toast } = useToast();

  useEffect(() => {
    const setupAuth = async () => {
      if (!publicKey) {
        console.log('No wallet connected');
        return;
      }

      const walletAddress = publicKey.toString();
      console.log('Setting up auth for wallet:', walletAddress);

      try {
        // Create a deterministic email and password based on the wallet address
        const email = `wallet_${walletAddress.toLowerCase()}@snubcube.com`;
        const password = `${walletAddress}_${process.env.VITE_AUTH_SALT || 'default_salt'}`;

        // Try to sign in first
        let { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        // If sign in fails, create a new user
        if (signInError) {
          console.log('User not found, creating new account...');
          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email,
            password,
          });

          if (signUpError) {
            console.error('Sign up error:', signUpError);
            throw signUpError;
          }

          // Wait for user creation and try signing in again
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          const { data: retrySignInData, error: retrySignInError } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (retrySignInError) {
            console.error('Retry sign in error:', retrySignInError);
            throw retrySignInError;
          }

          signInData = retrySignInData;
        }

        // Check and create profile if needed
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select()
          .eq('wallet_address', walletAddress)
          .maybeSingle();

        if (profileError) {
          console.error('Error checking profile:', profileError);
          throw profileError;
        }

        if (!profile) {
          console.log('No profile found, user will be prompted to create one');
          // Don't create profile automatically, let the Auth page handle it
          return;
        }

      } catch (error: any) {
        console.error('Authentication error:', error);
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description: error.message || "Failed to authenticate",
        });
      }
    };

    setupAuth();
  }, [publicKey, toast]);

  return <>{children}</>;
};

export const WalletContextProvider: FC<Props> = ({ children }) => {
  const network = WalletAdapterNetwork.Mainnet;
  
  // Use a more reliable RPC endpoint with fallbacks
  const endpoint = useMemo(() => {
    const endpoints = [
      'https://solana-mainnet.rpc.extrnode.com',
      'https://api.mainnet-beta.solana.com',
      'https://solana-api.projectserum.com'
    ];
    return endpoints[0]; // Use the first endpoint as primary
  }, []);

  const wallets = useMemo(
    () => [
      new SolflareWalletAdapter(),
    ],
    []
  );

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} autoConnect>
        <WalletModalProvider>
          <WalletAuthProvider>
            {children}
          </WalletAuthProvider>
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
};